import type { LidarClearanceAxis, LidarClearanceColorScale } from "./schemas";

const MISSING_VALUE_COLOR = "#FFFFFF";

// A color scale's colors are always gapless and 1-indexed by position, but
// the axis range they're previewed against may reach past either end -
// anything outside the scale's own coverage is filled with white rather
// than bleeding the nearest color into value territory it was never given.
export function buildGradient(
  colors: string[],
  min: number,
  max: number
): string | undefined {
  if (colors.length === 0) return undefined;

  const range = max - min;

  if (range === 0) {
    const color = min >= 1 && min <= colors.length ? colors[min - 1] : MISSING_VALUE_COLOR;
    return `linear-gradient(to right, ${color}, ${color})`;
  }

  const pct = (value: number) => Math.min(100, Math.max(0, ((value - min) / range) * 100));

  const parts: string[] = [];
  const firstPct = pct(1);
  if (firstPct > 0) {
    parts.push(`${MISSING_VALUE_COLOR} 0%`, `${MISSING_VALUE_COLOR} ${firstPct}%`);
  }
  colors.forEach((color, index) => parts.push(`${color} ${pct(index + 1)}%`));
  const lastPct = pct(colors.length);
  if (lastPct < 100) {
    parts.push(`${MISSING_VALUE_COLOR} ${lastPct}%`, `${MISSING_VALUE_COLOR} 100%`);
  }

  return `linear-gradient(to right, ${parts.join(", ")})`;
}

// A color scale only has to define as many colors as the axis has possible
// values to give every value a color - fewer colors than that leaves a gap.
export function colorScaleGapWarning(
  axis: LidarClearanceAxis,
  scale: LidarClearanceColorScale | undefined,
  formatMessage: (axisValueCount: number, scaleStopCount: number) => string
): string | null {
  if (!scale) return null;
  const axisValueCount = axis.max_value - axis.min_value + 1;
  if (axisValueCount <= scale.colors.length) return null;
  return formatMessage(axisValueCount, scale.colors.length);
}
